import { EnvironmentProviders, Injectable, inject } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import {
  ScheduledPaymentEventEventTypeMap,
  ScheduledPaymentWorkflowEvent,
  ScheduledPaymentWorkflowEventMetaDataMap,
} from './scheduled-payment-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Per-container tracker for Flow 9 (Scheduled Payments). Narrows `TrackWorkflowService` to
 * the scheduled-payment step enum + metadata map and exposes `startOperation` / `endOperation`
 * for stamping `operationDurationMs` on the SKIP / RESUME / AUTO_SKIP / AUTO_RESUME terminals.
 */
@Injectable()
export class ScheduledPaymentTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<ScheduledPaymentWorkflowEvent, ScheduledPaymentWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  trackWorkflow<E extends ScheduledPaymentWorkflowEvent>(step: E, metadata?: ScheduledPaymentWorkflowEventMetaDataMap[E]): void {
    this.tracker.trackWorkflow(step, metadata);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }
}

export function provideScheduledPaymentWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<ScheduledPaymentWorkflowEvent, ScheduledPaymentWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.SCHEDULED_PAYMENT]: ScheduledPaymentWorkflowEvent }),
    stepToEventType: ScheduledPaymentEventEventTypeMap,
  });
}
